import { useEffect, useMemo, useState } from 'react'

import { useDrawer } from '@fourthwall/components'

import type { LaunchStrategyData } from '../../../LaunchStrategy.types'
import { getAbsoluteGuideIndex } from '../../../utils/getAbsoluteGuideIndex'

type UseLaunchStrategyModalProps = {
  categories: LaunchStrategyData['modal']['categories']
  initialCategoryIndex?: number
  initialGuideIndex?: number
  onProgressChange: (categoryIndex: number, guideIndex: number) => void
}

export const useLaunchStrategyModal = ({
  categories,
  initialCategoryIndex,
  initialGuideIndex,
  onProgressChange,
}: UseLaunchStrategyModalProps) => {
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(initialCategoryIndex || 0)
  const [activeGuideIndex, setActiveGuideIndex] = useState(initialGuideIndex || 0)
  const drawer = useDrawer('launchStrategyDrawerID', { disableBodyScroll: true })

  useEffect(() => {
    onProgressChange(activeCategoryIndex, activeGuideIndex)
  }, [activeCategoryIndex, activeGuideIndex, onProgressChange])

  const absoluteActiveGuideIndex = useMemo(
    () => getAbsoluteGuideIndex(categories, activeCategoryIndex, activeGuideIndex),
    [activeCategoryIndex, activeGuideIndex, categories],
  )

  const handleChange = (categoryIndex: number, guideIndex: number) => {
    setActiveCategoryIndex(categoryIndex)
    setActiveGuideIndex(guideIndex)
  }

  const handleOnPrevious = () => {
    if (activeGuideIndex > 0) {
      setActiveGuideIndex(activeGuideIndex - 1)

      return
    }

    if (activeCategoryIndex > 0) {
      setActiveCategoryIndex(activeCategoryIndex - 1)
      setActiveGuideIndex(categories[activeCategoryIndex - 1].guides.length - 1)
    }
  }

  const handleOnNext = () => {
    if (activeGuideIndex < categories[activeCategoryIndex].guides.length - 1) {
      setActiveGuideIndex(activeGuideIndex + 1)

      return
    }

    if (activeCategoryIndex < categories.length - 1) {
      setActiveCategoryIndex(activeCategoryIndex + 1)
      setActiveGuideIndex(0)
    }
  }

  return {
    activeCategoryIndex,
    activeGuideIndex,
    absoluteActiveGuideIndex,
    guide: categories[activeCategoryIndex].guides[activeGuideIndex],
    drawer,
    onChange: handleChange,
    onPrevious: handleOnPrevious,
    onNext: handleOnNext,
  }
}
